import { SEOHead } from '@/components/seo/SEOHead';
import { PageHeader } from '@/components/layout/PageHeader';
import { DiscountCodeReveal } from '@/components/discount/DiscountCodeReveal';
import { useDiscountAvailability } from '@/hooks/useDiscountAvailability';

export function DiscountCodesPage() {
  const { offers: availableOffers, loaded, error } = useDiscountAvailability();
  const byTarget = new Map<string, (typeof availableOffers)[number]>();
  for (const offer of availableOffers) {
    const target = offer.productId
      ? `product:${offer.productId}`
      : offer.brandId
        ? `brand:${offer.brandId}`
        : null;
    if (target && !byTarget.has(target)) byTarget.set(target, offer);
  }
  const offers = [...byTarget.values()].sort((a, b) =>
    (a.productName || a.brandName || '').localeCompare(b.productName || b.brandName || '')
  );

  return (
    <>
      <SEOHead
        title="BBQ Gear Discount Codes and Current Offers"
        description="Find current BBQ gear discount codes and partner offers from Backyahd BBQ. Reveal available codes after verification and check retailer terms."
        canonicalUrl="/discount-codes"
        keywords={['bbq discounts', 'bbq gear offers', 'partner offers']}
      />
      <PageHeader title="Current Offers" subtitle="Partner offers available now" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="mb-6 text-gray-700">
          These offers come from our current partner records. Reveal an available code after
          verification, then check the terms with the retailer before ordering.
        </p>
        {!loaded && <p role="status">Loading current offers…</p>}
        {loaded && error && (
          <p role="alert">Current offers are unavailable. Please try again later.</p>
        )}
        {loaded && !error && offers.length === 0 && <p>No offers are currently available.</p>}
        {loaded && !error && offers.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            {offers.map((offer) => {
              const productId = offer.productId ?? undefined;
              const brandId = productId ? undefined : (offer.brandId ?? undefined);
              return (
                <section
                  key={productId ? `product:${productId}` : `brand:${brandId}`}
                  className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                >
                  <h2 className="text-lg font-semibold text-gray-900">
                    {offer.productName || offer.brandName || 'Partner offer'}
                  </h2>
                  <p className="mt-2 mb-4 text-sm text-gray-700">{offer.description}</p>
                  <DiscountCodeReveal
                    brandId={brandId}
                    productId={productId}
                    description={offer.description}
                    size="md"
                  />
                  <a
                    className="mt-3 block text-sm font-medium text-slate-800 underline"
                    href={`/gear?s=${encodeURIComponent(offer.productName || offer.brandName || '')}`}
                  >
                    Browse matching gear
                  </a>
                </section>
              );
            })}
          </div>
        )}
        <p className="mt-8 rounded bg-slate-100 p-5 text-sm text-gray-700">
          As an Amazon Associate, Backyahd BBQ earns from qualifying purchases.
        </p>
      </div>
    </>
  );
}
